import { lazy, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { ModelType } from '../../../types';
import { fetchWithType } from '../../../utils/api';
import {
  getAdminUrl,
  getApiToken,
  getUniqId,
  locationIsKia,
} from '../../../utils/helpers';
import { AppProps } from '../../App';
import CarouselCard from '../../components/carousel/CarouselCard';
import CarouselFilters from '../../components/carousel/CarouselFilters';
import { getStoreByKey } from '../../components/carousel/store/CarouselStore';
import { CarouselFieldsEnum } from '../../components/carousel/types/CarouselTypes';

const Snippet = lazy(() => import('../../components/Snippet'));
const CarouselControls = lazy(
  () => import('../../components/carousel/CarouselControls'),
);

const maybeUniqId = getUniqId();

const PriceCard = ({
  pricelist,
  privateMileages,
  fields,
  imageType,
  modelCode,
  hideControls,
  uniqKey,
}: { hideControls?: boolean; uniqKey?: string } & Pick<
  AppProps,
  'pricelist' | 'fields' | 'imageType' | 'modelCode' | 'privateMileages'
>) => {
  const uniqId = uniqKey || maybeUniqId;
  const { dispatch, options, direction, showPrivateMileages } = useStore(
    getStoreByKey(uniqId),
  );

  const [model, setModel] = useState<ModelType>();
  const [models, setModels] = useState<ModelType[]>([]);

  const setData = () => {
    dispatch({
      type: 'SHOW_PRICELIST',
      payload: Boolean(pricelist),
    });
    dispatch({
      type: 'SHOW_PRIVATE_MILEAGES',
      payload: privateMileages === 'true',
    });
    if (imageType) {
      dispatch({ type: 'IMAGE_TYPE', payload: imageType });
    }
    if (fields) {
      dispatch({
        type: 'CONTROLS',
        payload: fields.split(',') as CarouselFieldsEnum[],
      });
    }
    dispatch({ type: 'IS_LOADED', payload: true });
  };

  const fetchModels = async () => {
    if (!locationIsKia()) {
      let models =
        JSON.parse(sessionStorage.getItem('kia-carousel-models')) ?? [];
      if (!models.length) {
        try {
          const { data } = await fetchWithType<ModelType[]>(
            getAdminUrl() + '/api/models',
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${getApiToken()}`,
              },
            },
          );
          if (data && data.length) {
            setModels(data);
          }
        } catch (error) {
          console.error('Error fetching trims:', error);
        }
      } else {
        setModels(models);
      }
    }
    if (modelCode) {
      try {
        const { data } = await fetchWithType<ModelType>(
          getAdminUrl() + '/api/model/' + modelCode,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${getApiToken()}`,
            },
          },
        );
        if (data && data.id) {
          setModel(data);
        }
        setData();
      } catch (error) {
        console.warn('Error fetching model:', modelCode);
      }
    } else {
      setModel(models[0]);
    }
  };

  useEffect(() => {
    fetchModels();
    dispatch({ type: 'DIRECTION', payload: 'vertical' });
  }, []);

  const getSnippetProps: () => string = () => {
    return `modelCode="${model?.code}" ${
      !options
        ? `pricelist="true" privateMileages="${showPrivateMileages}"`
        : Object.entries(options)
            .map(([key, value]) => (value ? `${key}="${value}"` : ''))
            .filter(Boolean)
            .join(' ')
    }`;
  };

  return (
    <div
      className={`pt-4 pb-12 ${direction === 'vertical' ? 'max-w-screen-kia-sm mx-auto' : ''}`}
    >
      {!locationIsKia() && !hideControls ? (
        <>
          {models?.length ? (
            <select
              onChange={(e) => {
                const model = models.find((m) => m.code === e.target.value);
                if (model) {
                  setModel(model);
                }
              }}
              className="border border-black p-1 w-full"
            >
              <option value="">Välj modell</option>
              {models.map((model) => (
                <option key={model.id} value={model.code}>
                  {model.name}
                </option>
              ))}
            </select>
          ) : null}
          <CarouselControls uniqId={uniqId} type="single" />
          <Snippet page="pricecard" props={getSnippetProps()} />
        </>
      ) : null}
      <div className="max-w-screen-kia-sm mx-auto relative">
        {!options ||
        options?.fields.includes(
          CarouselFieldsEnum.PRIVATE_LEASING ||
            CarouselFieldsEnum.BUSINESS_LEASE,
        ) ? (
          <>
            <CarouselFilters uniqId={uniqId} single />
          </>
        ) : null}
      </div>
      <div className="px-4 lg:px-0">
        {model ? (
          <CarouselCard uniqId={uniqId} key={model.code} model={model} />
        ) : null}
      </div>
    </div>
  );
};

export default PriceCard;
